"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { COLLECTION_LISTINGS_KEY } from "@/features/collections/hooks/useCollectionListings";
import ListDialog from "./dialogs/ListDialog";
import OfferDialog from "./dialogs/OfferDialog";
import { CancelListingDialog, DecisionDialog } from "./dialogs/ReviewOnlyDialogs";
import type { NftFinance } from "./useNftFinance";

/**
 * The five money paths, mounted exactly as the legacy board mounted them —
 * same flows, same props, same `onSuccess` (an ownership change refreshes the
 * server-rendered page, an offer change refetches the offers, an acceptance
 * does both). Each is mounted only while it is open, so a closed flow holds no
 * stale amount and fires no balance query. Each is a v2 dialog over a hook that
 * holds the legacy flow's code (`features/asset/flows/`).
 */
export default function FinanceFlows(t0) {
  const $ = _c(52);
  const {
    f
  } = t0;
  const {
    flow,
    shared,
    closeFlow,
    router,
    refreshOffers
  } = f;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== queryClient || $[1] !== router) {
    t1 = () => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_LISTINGS_KEY]
      });
      router.refresh();
    };
    $[0] = queryClient;
    $[1] = router;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const listingChanged = t1;
  let t2;
  if ($[3] !== closeFlow || $[4] !== f.currencyId || $[5] !== f.networkId || $[6] !== f.networkName || $[7] !== flow?.kind || $[8] !== listingChanged || $[9] !== shared) {
    t2 = flow?.kind === "list" && <ListDialog {...shared} networkName={f.networkName} networkId={f.networkId} currencyId={f.currencyId} onClose={closeFlow} onSuccess={listingChanged} />;
    $[3] = closeFlow;
    $[4] = f.currencyId;
    $[5] = f.networkId;
    $[6] = f.networkName;
    $[7] = flow?.kind;
    $[8] = listingChanged;
    $[9] = shared;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== closeFlow || $[12] !== f.currencySymbol || $[13] !== f.isListed || $[14] !== f.listingCurrencySymbol || $[15] !== f.listingPrice || $[16] !== f.networkId || $[17] !== flow?.kind || $[18] !== refreshOffers || $[19] !== shared) {
    t3 = flow?.kind === "offer" && <OfferDialog {...shared} fallbackSymbol={f.currencySymbol} networkId={f.networkId} listingFloor={f.isListed ? f.listingPrice ?? null : null} listingFloorSymbol={f.listingCurrencySymbol} onClose={closeFlow} onSuccess={refreshOffers} />;
    $[11] = closeFlow;
    $[12] = f.currencySymbol;
    $[13] = f.isListed;
    $[14] = f.listingCurrencySymbol;
    $[15] = f.listingPrice;
    $[16] = f.networkId;
    $[17] = flow?.kind;
    $[18] = refreshOffers;
    $[19] = shared;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  let t4;
  if ($[21] !== closeFlow || $[22] !== f.currencySymbol || $[23] !== f.listingCurrencyAddress || $[24] !== f.listingCurrencySymbol || $[25] !== f.listingPrice || $[26] !== f.listingQuantity || $[27] !== f.networkId || $[28] !== f.networkName || $[29] !== flow?.kind || $[30] !== listingChanged || $[31] !== shared) {
    t4 = flow?.kind === "cancel-listing" && <CancelListingDialog {...shared} networkName={f.networkName} networkId={f.networkId} fallbackSymbol={f.currencySymbol} listingPrice={f.listingPrice ?? null} listingQuantity={f.listingQuantity} listingCurrencyAddress={f.listingCurrencyAddress} listingCurrencySymbol={f.listingCurrencySymbol} onClose={closeFlow} onSuccess={listingChanged} />;
    $[21] = closeFlow;
    $[22] = f.currencySymbol;
    $[23] = f.listingCurrencyAddress;
    $[24] = f.listingCurrencySymbol;
    $[25] = f.listingPrice;
    $[26] = f.listingQuantity;
    $[27] = f.networkId;
    $[28] = f.networkName;
    $[29] = flow?.kind;
    $[30] = listingChanged;
    $[31] = shared;
    $[32] = t4;
  } else {
    t4 = $[32];
  }
  let t5;
  if ($[33] !== closeFlow || $[34] !== f.currencySymbol || $[35] !== flow || $[36] !== listingChanged || $[37] !== refreshOffers || $[38] !== shared) {
    t5 = flow?.kind === "accept-offer" && <DecisionDialog {...shared} mode="accept" offer={flow.offer} fallbackSymbol={f.currencySymbol} onClose={closeFlow} onSuccess={async () => {
      await refreshOffers();
      listingChanged();
    }} />;
    $[33] = closeFlow;
    $[34] = f.currencySymbol;
    $[35] = flow;
    $[36] = listingChanged;
    $[37] = refreshOffers;
    $[38] = shared;
    $[39] = t5;
  } else {
    t5 = $[39];
  }
  let t6;
  if ($[40] !== closeFlow || $[41] !== f.currencySymbol || $[42] !== flow || $[43] !== refreshOffers || $[44] !== shared) {
    t6 = flow?.kind === "cancel-offer" && <DecisionDialog {...shared} mode="cancel" offer={flow.offer} fallbackSymbol={f.currencySymbol} onClose={closeFlow} onSuccess={refreshOffers} />;
    $[40] = closeFlow;
    $[41] = f.currencySymbol;
    $[42] = flow;
    $[43] = refreshOffers;
    $[44] = shared;
    $[45] = t6;
  } else {
    t6 = $[45];
  }
  let t7;
  if ($[46] !== t2 || $[47] !== t3 || $[48] !== t4 || $[49] !== t5 || $[50] !== t6) {
    t7 = <>{t2}{t3}{t4}{t5}{t6}</>;
    $[46] = t2;
    $[47] = t3;
    $[48] = t4;
    $[49] = t5;
    $[50] = t6;
    $[51] = t7;
  } else {
    t7 = $[51];
  }
  return t7;
}